import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Wallet, TrendingUp, Clock } from "lucide-react";
import { StatCard, PaymentBadge, Loading, EmptyState, Money } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Payments() {
  const [orders, setOrders] = useState(null);
  const [payments, setPayments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/orders"), api.get("/payments")]).then(([o, p]) => { setOrders(o.data); setPayments(p.data); }).catch(() => setOrders(false));
  }, []);

  if (orders === null) return <Loading />;
  if (!orders) return <EmptyState title="Unable to load payments" />;

  const collected = orders.reduce((s, o) => s + Number(o.paid_amount || 0), 0);
  const pending = orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + Math.max(o.total_amount - o.paid_amount, 0), 0);
  const totalValue = orders.reduce((s, o) => s + Number(o.total_amount || 0), 0);
  const pendingOrders = orders.filter((o) => o.payment_status !== "paid" && o.status !== "cancelled");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Payments</h1>
        <p className="text-sm text-slate-500 mt-1">Track collected and pending payments across orders.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={TrendingUp} tone="emerald" label="Revenue Collected" value={<Money value={collected} />} sub={`of ${'₹' + totalValue.toLocaleString("en-IN")} total`} />
        <StatCard icon={Clock} tone="rose" label="Pending Payments" value={<Money value={pending} />} sub={`${pendingOrders.length} orders`} />
        <StatCard icon={Wallet} tone="blue" label="Total Order Value" value={<Money value={totalValue} />} sub={`${orders.length} orders`} />
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending-pay">Pending ({pendingOrders.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-pay-history">Payment History ({payments.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
            {pendingOrders.length === 0 ? <EmptyState icon={Wallet} title="No pending payments" /> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Pending</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pendingOrders.map((o) => (
                    <TableRow key={o.order_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/${o.order_id}`)}>
                      <TableCell className="font-mono font-medium">{o.order_id}</TableCell>
                      <TableCell>{o.business_name}</TableCell>
                      <TableCell><Money value={o.total_amount} /></TableCell>
                      <TableCell className="text-emerald-700"><Money value={o.paid_amount} /></TableCell>
                      <TableCell className="text-rose-700"><Money value={Math.max(o.total_amount - o.paid_amount, 0)} /></TableCell>
                      <TableCell><PaymentBadge status={o.payment_status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="history">
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
            {payments.length === 0 ? <EmptyState icon={Wallet} title="No payments recorded" /> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Order</TableHead><TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Reference</TableHead></TableRow></TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/${p.order_id}`)}>
                      <TableCell>{formatDate(p.payment_date)}</TableCell>
                      <TableCell className="font-mono font-medium">{p.order_id}</TableCell>
                      <TableCell><Money value={p.amount} /></TableCell>
                      <TableCell>{p.payment_method || "—"}</TableCell>
                      <TableCell className="text-slate-500">{p.transaction_ref || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
